<template>
  <div>
    <b-row v-if="!setGlobalLoading && errorsMessage && errorsMessage.length" class="alert-messages pt-3">
      <b-col>
        <b-alert
          show dismissible
          variant="danger"
          v-for="(alertMessage, index) in errorsMessage"
          :key="'alert-msg-' + index.toString()"
          class="alert-height pr-5"
        ><div v-html="alertMessage"></div></b-alert>
      </b-col>
    </b-row>
    <StepTitle>
      <div v-html="title" class="how-can"></div>
      <template v-if="isMFA" #subtitle>
        <div class="customer-form-subtitle">
          <span v-if="showMFAChoices">Select how you would like to receive your verification code:</span>
          <span v-else-if="selectedMFAMethod">Please enter the 6-digit code we just {{MFAAction}}</span>
        </div>
      </template>
    </StepTitle>
    <b-form @submit.prevent="submit()" class="customer-verify-form">
      <div class="customer-form-wrapper">
        <template v-if="isMFA">
          <template v-if="showMFAChoices">
            <b-row class="justify-content-md-center boxes">
              <b-col class="customer-match-form">
                <div class="choice-fields-input">
                  <b-form-group label-for="mfa-method">
                    <b-form-radio-group id="mfa-method" v-model="selectedMFAMethod" name="mfa-method" stacked>
                      <b-form-radio v-for="option in MFAChoices" :key="option.value" :value="option.value"
                                    :class="{'selected': option.value === selectedMFAMethod}" :for="'option-'+option.value"
                                    :id="'option-'+option.value">
                        <div>{{option.label}}</div>
                      </b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </div>
              </b-col>
            </b-row>
          </template>
          <template v-else-if="selectedMFAMethod">
            <b-row class="justify-content-md-center boxes">
              <b-col class="customer-match-form">
                <div class="customer-match-formgroup">
                  <label for="mfa-code" >Verification Code</label>
                  <b-form-group label-for="mfa-code" class="highlighted">
                    <div data-for="mfa-code" class="placeholder-label">XXXXXX</div>
                    <div class="pt-0">
                      <b-form-input v-model="MFACode" id="mfa-code" type="text" class="customer form-input-height"></b-form-input>
                    </div>
                  </b-form-group>
                </div>
              </b-col>
            </b-row>
          </template>
        </template>
        <template v-else>
          <VerificationField v-for="(field, index) in session.required_fields"
                             :key="'verification-field-' + index.toString()"
                             :method="field"
                             :value="values"
                             :placeholders="placeholders"
                             all-fields-required
          >
          </VerificationField>
          <input id="country" type="hidden" v-model="values.country" />
        </template>
      </div>
      <div class="customer-form-wrapper customer-form-btn-wrapper">
        <button v-if="session && session.can_undo && setGlobalLoading" @click="goBack()" class="btn-base input-choice-btn input-btn new-btn-outline-success stroke-2x" type="button">Back</button>
        <button v-if="isMFA && !showMFAChoices && selectedMFAMethod" @click="selectMFAMethod()" class="btn-base input-choice-btn input-btn text-nowrap" type="button">Send a new code</button>
        <button @click="$event.stopPropagation()" :class="{'submit-and-verify': !setGlobalLoading}" type="submit" class="btn-base input-choice-btn input-btn text-nowrap">
          <template v-if="isMFA && showMFAChoices"> Send Code </template>
          <template v-else-if="setGlobalLoading"> Submit </template>
          <template v-else>
            SUBMIT & CONTINUE
            <span class="float-none float-sm-right pr-2">
              <font-awesome-icon :icon="['fas', 'arrow-right']"></font-awesome-icon>
            </span>
          </template>
        </button>
      </div>
    </b-form>
    <div ref="translation_detector" style="display:none;">{{translateDetectString}}</div>
    <b-link block class="centered-block close-button" variant="secondary" v-if="session && session.is_popup && !session.can_undo" @click="goBack()">&times;</b-link>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  CustomerVerifyMessages,
  TranslateTestString,
  SessionResult,
  SessionStatus,
  MFAStatus,
  MFAMethod
} from '@/constants'
import {setPlaceholders, stopMouseflowRecording, getErrorData} from '@/utils'
import StepTitle from './StepTitle'
import VerificationField from './VerificationField'

export default {
  name: 'CustomerVerify',
  components: {
    VerificationField,
    StepTitle
  },
  props: {
    getCart: {
      type: Boolean,
      default: true
    },
    setGlobalLoading: {
      type: Boolean,
      default: true
    }
  },
  data: function() {
    return {
      translateDetectString: TranslateTestString,
      values: {
        first_name: null,
        last_name: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        phone: null,
        email: null,
        country: null
      },
      placeholders: {
        first_name: '',
        last_name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: '',
        country: ''
      },
      errorsMessage: null,
      showMFAChoices: false,
      selectedMFAMethod: null,
      MFAChoices: [],
      MFACode: null
    }
  },
  computed: {
    ...mapState(['session', 'cartIsOpen']),
    isMFA: function() {
      return this.session && (this.session.status === SessionStatus.Verified) &&
        (this.session.mfa_status === MFAStatus.Pending)
    },
    title: function() {
      return this.isMFA ? (this.showMFAChoices ? 'Send verification code' : 'Enter code to continue') : this.session.verify_message
    },
    MFAAction: function() {
      if (this.selectedMFAMethod === MFAMethod.SMS) {
        return 'texted to you'
      } else if (this.selectedMFAMethod === MFAMethod.Email) {
        return 'emailed to you'
      } else if (this.selectedMFAMethod === MFAMethod.Voice) {
        return 'called you with'
      }
      return 'sent to you'
    }
  },
  methods: {
    obfuscateEmail: function(email) {
      const parts = email.split('@')
      if (parts.length !== 2) {
        return email
      }
      const name = parts[0]
      const domain = parts[1]
      let obfuscatedName = ''
      if (name.length <= 2) {
        obfuscatedName = name[0] + '*'
      } else {
        obfuscatedName = name[0] + '*'.repeat(name.length - 2) + name[name.length - 1]
      }
      const domainParts = domain.split('.')
      if (domainParts.length < 2) {
        return obfuscatedName + '@' + domain
      }
      const domainName = domainParts.slice(0, -1).join('.')
      const tld = domainParts[domainParts.length - 1]
      let obfuscatedDomainName = ''
      if (domainName.length <= 2) {
        obfuscatedDomainName = domainName[0] + '*'
      } else {
        obfuscatedDomainName = domainName[0] + '*'.repeat(domainName.length - 2) + domainName[domainName.length - 1]
      }
      return obfuscatedName + '@' + obfuscatedDomainName + '.' + tld
    },
    obfuscatePhone: function(phone, countryCode) {
      const digits = phone.replace(/\D/g, '')
      const nationalNumber = digits.startsWith(countryCode) ? digits.slice(countryCode.length) : digits

      if (countryCode === '1') {
        // North American Numbering Plan
        return `(${nationalNumber.slice(0, 3)}) *** - **` + nationalNumber.slice(-2)
      }

      // International format
      return `+${countryCode}-` + nationalNumber.slice(0, 2) + '*'.repeat(nationalNumber.length - 4) +
        nationalNumber.slice(-2)
    },
    updateData: function(field, val) {
      this.values[field] = val
    },
    processPostMatch: function(session) {
      if (this.getCart) {
        this.$store.dispatch('get_cart')
      }
      this.init()
      this.$emit('done')
      if (!this.setGlobalLoading) {
        this.$emit('verifying', false)
      }
    },
    updateMFAChoices: function() {
      const choices = []

      if (this.session && this.session.customer) {
        if (this.session.customer.phone) {
          choices.push({value: MFAMethod.SMS,
            label: 'Text me at ' + this.obfuscatePhone(
              this.session.customer.phone, this.session.customer.phone_country_code)})
        }
        if (this.session.customer.email) {
          choices.push({value: MFAMethod.Email,
            label: 'Email me at ' + this.obfuscateEmail(this.session.customer.email)})
        }

        if (this.session.mfa_method === MFAMethod.Email) {
          choices.reverse()
        }
      }

      this.MFAChoices = choices

      if (choices.length) {
        this.selectedMFAMethod = choices[0].value
      }
    },
    selectMFAMethod() {
      this.MFACode = null
      this.updateMFAChoices()
      this.showMFAChoices = true
    },
    sendMFACode() {
      if (this.selectedMFAMethod) {
        this.$store.commit('busy')
        this.$store.dispatch('mfa_send', {method: this.selectedMFAMethod})
          .then(session => {
            this.MFACode = null
            this.showMFAChoices = false
            this.$store.commit('ready')
          })
          .catch(err => {
            if (err.response && err.response.status === 400) {
              let errors = []

              Object.keys(err.response.data).forEach(key => {
                errors = errors.concat(err.response.data[key])
              })
              if (!this.setGlobalLoading) {
                this.errorsMessage = errors
              } else {
                this.$store.commit('error', errors)
              }
            } else {
              this.$store.commit('critical_error', err)
            }
            this.$store.commit('ready')
          })
      }
    },
    verify: function() {
      this.$root.$emit('toggle-processing-gifs', CustomerVerifyMessages)
      if (this.setGlobalLoading) {
        this.$store.commit('busy')
      } else {
        this.$emit('verifying', true)
      }
      const path = this.isMFA ? 'mfa_verify' : 'verify'
      const params = this.isMFA ? {code: this.MFACode} : this.values
      this.$store.dispatch(path, params)
        .then(session => this.processPostMatch(session))
        .catch(err => {
          if (err.response && err.response.status === 404) {
            let message = this.isMFA ? 'Sorry, but the code you entered is invalid.'
              : "Sorry, but we couldn't verify your information." +
              "<br/>Make sure you typed it correctly and it's the same as when you placed your order."
            if (!this.setGlobalLoading) {
              this.errorsMessage = [message]
            } else {
              this.$store.commit('error', message)
            }
          } else if (err.response && err.response.status === 403) {
            const error = getErrorData(err)
            console.log(error)
            this.$store.commit('no_match', error.message, SessionResult.LifelineNoVerify)
          } else if (err.response && err.response.status === 400) {
            let errors = []

            Object.keys(err.response.data).forEach(key => {
              errors = errors.concat(err.response.data[key])
            })
            if (!this.setGlobalLoading) {
              this.errorsMessage = errors
            } else {
              this.$store.commit('error', errors)
            }
          } else if (err.response && err.response.status === 303 && err.response.data) {
            this.$store.commit('session', err.response.data)
            this.processPostMatch(err.response.data)
          } else {
            this.$store.commit('critical_error', err)
          }
          if (!this.setGlobalLoading) {
            this.$emit('verifying', false)
          } else {
            this.$store.commit('ready')
          }
          this.MFACode = null // Clear code on error
        })
        .finally(() => this.$root.$emit('toggle-processing-gifs', null))
    },
    submit: function() {
      if (this.isMFA && this.showMFAChoices) {
        this.sendMFACode()
      } else {
        this.verify()
      }
    },
    goBack: function() {
      this.$emit('back')
    },
    init: function() {
      if (this.isMFA) {
        if (this.session.mfa_method) {
          this.showMFAChoices = false
          this.selectedMFAMethod = this.session.mfa_method
        } else {
          this.selectMFAMethod()
        }
      }
    }
  },
  mounted() {
    stopMouseflowRecording()
    this.$store.dispatch('wait_for_translation', {element: this.$refs['translation_detector']})
      .then(value => {
        setTimeout(() => {
          setPlaceholders(this.placeholders)
        }, 100)
      })
  },
  beforeDestroy() {
    this.$root.$emit('toggle-processing-gifs', null)
  },
  created() {
    this.init()
  }
}
</script>

<style scoped>

</style>
